// src/components/shared/FAQ.tsx

/**
 * FAQ Component
 * 
 * A flexible FAQ component that supports categorization, theming, and schema markup.
 * Built on top of Radix UI's Accordion primitive for accessibility and customization.
 * 
 * Features:
 * - Single or multiple expanded sections
 * - Optional categorization of FAQ items
 * - Customizable theming (default, integrated, minimal variants)
 * - Schema.org FAQ markup for SEO (can be disabled)
 * - Icon support for questions
 * - Responsive design with dark mode support
 * 
 * @module components/shared/FAQ
 * @example
 * ```tsx
 * // Basic usage with array of FAQ items
 * <FAQ
 *   items={[
 *     {
 *       question: "How does it work?",
 *       answer: "It works like this...",
 *       category: "General",
 *       icon: <InfoIcon />
 *     }
 *   ]}
 *   theme={{ variant: "integrated", size: "md" }}
 *   features={{ showIcons: true, categorized: true }}
 * />
 * 
 * // Alternative usage with direct faq prop (used in GeneratorLayout)
 * <FAQ faq={resource.faqs} />
 * ```
 */

import { type ComponentPropsWithoutRef, type ElementRef, type ReactNode, forwardRef, isValidElement, useMemo } from "react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/Accordion"
import { cx } from "~/lib/utils"
import type * as AccordionPrimitive from "@radix-ui/react-accordion"
import { JsonLd } from 'react-schemaorg'
import type { FAQPage } from 'schema-dts'
import { sendEvent, createEngagementStopwatch } from "~/lib/gtag"

/**
 * Flatten a ReactNode answer to plain text for the FAQPage JSON-LD.
 *
 * The `acceptedAnswer.text` must be a string, but `answer` accepts rich
 * `ReactNode` content. Previously a non-string answer shipped a
 * `"See website for detailed answer"` placeholder, which defeats the AEO
 * grounding the schema exists for. Recursively concatenate the text children so
 * a `<p>…</p>` / fragment answer still contributes its real words to the schema.
 */
export function reactNodeToPlainText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(reactNodeToPlainText).join("")
  if (isValidElement<{ children?: ReactNode }>(node)) return reactNodeToPlainText(node.props.children)
  return ""
}

/**
 * Represents a single FAQ item
 * 
 * @interface FAQItem
 * @property {string} question - The question text
 * @property {ReactNode} answer - The answer content - can be text or React nodes
 * @property {string} [category] - Optional category for grouping FAQs
 * @property {ReactNode} [icon] - Optional icon to display next to the question
 */
export interface FAQItem {
  /** The question text */
  question: string
  /** The answer content - can be text or React nodes */
  answer: ReactNode
  /** Optional category for grouping FAQs */
  category?: string
  /** Optional icon to display next to the question */
  icon?: ReactNode
}

/**
 * Theme configuration for the FAQ component
 * 
 * @interface FAQTheme
 * @property {("default"|"integrated"|"minimal")} [variant="default"] - Visual style variant
 * @property {("xs"|"sm"|"md"|"lg"|"xl"|"2xl")} [size="md"] - Size preset affecting spacing and typography
 */
export interface FAQTheme {
  /** Visual style variant */
  variant?: "default" | "integrated" | "minimal"
  /** Size preset affecting spacing and typography */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
}

/**
 * Feature flags to control FAQ component behavior
 * 
 * @interface FAQFeatures
 * @property {boolean} [showTitle=true] - Show/hide the FAQ title
 * @property {boolean} [showDescription=true] - Show/hide the FAQ description
 * @property {boolean} [showIcons=false] - Show/hide icons next to questions
 * @property {boolean} [categorized=false] - Enable/disable category grouping
 * @property {boolean} [allowMultiple=false] - Allow multiple sections to be expanded
 * @property {boolean} [animated=true] - Enable/disable animations
 */
export interface FAQFeatures {
  /** Show/hide the FAQ title */
  showTitle?: boolean
  /** Show/hide the FAQ description */
  showDescription?: boolean
  /** Show/hide icons next to questions */
  showIcons?: boolean
  /** Enable/disable category grouping */
  categorized?: boolean
  /** Allow multiple sections to be expanded */
  allowMultiple?: boolean
  /** Enable/disable animations */
  animated?: boolean
}

/**
 * Main FAQ component props
 * 
 * @interface FAQProps
 * @property {FAQItem[]} items - Array of FAQ items to display
 * @property {string} [title="Frequently Asked Questions"] - Optional title text
 * @property {string} [description] - Optional description text
 * @property {FAQTheme} [theme] - Theme configuration
 *   @property {("default"|"integrated"|"minimal")} [theme.variant="default"] - Visual style variant
 *   @property {("xs"|"sm"|"md"|"lg"|"xl"|"2xl")} [theme.size="md"] - Size preset affecting spacing and typography
 * @property {FAQFeatures} [features] - Feature flags
 *   @property {boolean} [features.showTitle=true] - Show/hide the FAQ title
 *   @property {boolean} [features.showDescription=true] - Show/hide the FAQ description
 *   @property {boolean} [features.showIcons=false] - Show/hide icons next to questions
 *   @property {boolean} [features.categorized=false] - Enable/disable category grouping
 *   @property {boolean} [features.allowMultiple=false] - Allow multiple sections to be expanded
 *   @property {boolean} [features.animated=true] - Enable/disable animations
 * @property {boolean} [disableSchema=false] - Whether to disable FAQ schema generation
 */
export interface FAQProps {
  /** Array of FAQ items to display */
  items: FAQItem[]
  /** Optional title text */
  title?: string
  /** Optional description text */
  description?: string
  /** Theme configuration */
  theme?: FAQTheme
  /** Feature flags */
  features?: FAQFeatures
  /** Whether to disable FAQ schema generation */
  disableSchema?: boolean
}

/**
 * Props for single-selection accordion mode
 * 
 * @typedef {Object} AccordionSingleProps
 * @property {string} type - Must be "single" for single selection mode
 * @property {string} [value] - Controlled value for the accordion
 * @property {string} [defaultValue] - Default value for uncontrolled accordion
 * @property {Function} [onValueChange] - Callback when value changes
 *   @param {string} onValueChange.value - The new selected value
 * 
 * @extends {ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>}
 */
type AccordionSingleProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
  type: "single"
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
}

/**
 * Props for multiple-selection accordion mode
 * 
 * @typedef {Object} AccordionMultipleProps
 * @property {string} type - Must be "multiple" for multiple selection mode
 * @property {string[]} [value] - Controlled values for the accordion
 * @property {string[]} [defaultValue] - Default values for uncontrolled accordion
 * @property {Function} [onValueChange] - Callback when values change
 *   @param {string[]} onValueChange.value - The new selected values
 * 
 * @extends {ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>}
 */
type AccordionMultipleProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
  type: "multiple"
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
}

/**
 * Union type for accordion props
 * 
 * @typedef {AccordionSingleProps | AccordionMultipleProps} AccordionProps
 */
type AccordionProps = AccordionSingleProps | AccordionMultipleProps

/**
 * FAQ Component implementation
 * 
 * Renders a customizable FAQ section with optional categorization,
 * theming, and Schema.org markup for SEO.
 *
 * @component
 * @param {Object} props - Component props
 * @param {FAQItem[]} props.items - Array of FAQ items to display
 * @param {string} [props.title="Frequently Asked Questions"] - Optional title text
 * @param {string} [props.description] - Optional description text
 * @param {FAQTheme} [props.theme] - Theme configuration with variant and size
 * @param {("default"|"integrated"|"minimal")} [props.theme.variant="default"] - Visual style variant
 * @param {("xs"|"sm"|"md"|"lg"|"xl"|"2xl")} [props.theme.size="md"] - Size preset affecting spacing and typography
 * @param {FAQFeatures} [props.features] - Feature flags for controlling display options
 * @param {boolean} [props.features.showTitle=true] - Show/hide the FAQ title
 * @param {boolean} [props.features.showDescription=true] - Show/hide the FAQ description
 * @param {boolean} [props.features.showIcons=false] - Show/hide icons next to questions
 * @param {boolean} [props.features.categorized=false] - Enable/disable category grouping
 * @param {boolean} [props.features.allowMultiple=false] - Allow multiple sections to be expanded
 * @param {boolean} [props.features.animated=true] - Enable/disable animations
 * @param {string} [props.className] - Additional CSS classes
 * @param {"single"|"multiple"} [props.type="single"] - Accordion type: "single" allows only one item open at a time, "multiple" allows multiple items open simultaneously
 * @param {string|string[]} [props.value] - Controlled value(s) for accordion: string for "single" type, string[] for "multiple" type
 * @param {string|string[]} [props.defaultValue] - Default value(s) for accordion: string for "single" type, string[] for "multiple" type
 * @param {Function} [props.onValueChange] - Value change handler: (value: string) => void for "single" type, (value: string[]) => void for "multiple" type
 * @param {boolean} [props.collapsible=true] - Whether the open item can be collapsed
 * @param {React.Ref<ElementRef<typeof Accordion>>} ref - Forwarded ref
 * @param {boolean} [props.disableSchema=false] - Whether to disable FAQ schema generation
 * @returns {JSX.Element} Rendered FAQ component
 * @example
 * // Basic usage with array of FAQ items
 * <FAQ
 *   items={[
 *     {
 *       question: "How does it work?",
 *       answer: "It works like this...",
 *       category: "General",
 *       icon: <InfoIcon />
 *     }
 *   ]}
 *   theme={{ variant: "integrated", size: "md" }}
 *   features={{ showIcons: true, categorized: true }}
 * />
 */
export const FAQ = forwardRef<
  ElementRef<typeof Accordion>,
  FAQProps & Omit<AccordionProps, keyof FAQProps>
>(({ 
  items,
  title = "Frequently Asked Questions",
  description,
  theme = {
    variant: "default",
    size: "md"
  },
  features = {},
  disableSchema = false,
  className,
  type = "single",
  ...props
}, ref) => {
  // Memoize the faqItems to prevent unnecessary re-renders
  const faqItems = useMemo(() => items ?? [], [items])
  const { showTitle = true, showDescription = true } = features
  
  // Engagement stopwatch for interaction deltas
  const getDeltaMs = useMemo(() => createEngagementStopwatch(), [])

  // Group FAQ items by category
  const categories = useMemo(() => {
    return faqItems.reduce<Record<string, FAQItem[]>>((acc, item) => {
      const category = item.category ?? "General"
      acc[category] ??= []
      acc[category].push(item)
      return acc
    }, {})
  }, [faqItems])

  const hasCategories = Object.keys(categories).length > 1

  const accordionProps = {
    ref,
    type,
    collapsible: true,
    className: "w-full",
    ...props
  } as AccordionProps

  // Size-specific CSS classes for responsive design
  const sizeClasses = {
    xs: {
      container: "p-4",
      title: "text-base mb-1",
      description: "mb-3 text-sm",
      categoryTitle: "mb-2 text-base",
      question: "py-2 text-xs",
      answer: "pb-2 pt-0 text-xs"
    },
    sm: {
      container: "p-6",
      title: "text-lg mb-2",
      description: "mb-4 text-sm",
      categoryTitle: "mb-3 text-base",
      question: "py-3 text-sm",
      answer: "pb-3 pt-0 text-sm"
    },
    md: {
      container: "p-8",
      title: "text-xl mb-2",
      description: "mb-6 text-base",
      categoryTitle: "mb-4 text-lg",
      question: "py-4 text-sm",
      answer: "pb-4 pt-0 text-sm"
    },
    lg: {
      container: "p-10",
      title: "text-2xl mb-3",
      description: "mb-8 text-base",
      categoryTitle: "mb-5 text-xl",
      question: "py-3 text-base",
      answer: "pb-3 pt-0 text-base"
    },
    xl: {
      container: "p-10",
      title: "text-3xl mb-3",
      description: "mb-10 text-lg",
      categoryTitle: "mb-6 text-2xl",
      question: "py-3 text-lg",
      answer: "pb-3 pt-0 text-base"
    },
    "2xl": {
      container: "p-12",
      title: "text-4xl mb-4",
      description: "mb-10 text-xl",
      categoryTitle: "mb-6 text-2xl",
      question: "py-4 text-xl",
      answer: "pb-4 pt-0 text-lg"
    }
  }

  const size = theme.size ?? "md"
  const sizeClass = sizeClasses[size]

  return (
    <>
      {/* Schema.org structured data for SEO - only if not disabled */}
      {!disableSchema && (
        <JsonLd<FAQPage>
          item={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(faq => ({
              "@type": "Question" as const,
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer" as const,
                "text": (typeof faq.answer === 'string' ? faq.answer : reactNodeToPlainText(faq.answer)).trim() || 'See website for detailed answer'
              }
            }))
          }}
        />
      )}

      <div className={cx(
        "w-full",
        theme.variant === "integrated" && `rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 ${sizeClass.container}`,
        className
      )}>
        {showTitle && title && (
          <h3 className={cx(
            "font-semibold text-gray-900 dark:text-white",
            theme.variant === "minimal" && "text-lg",
            sizeClass.title
          )}>
            {title}
          </h3>
        )}
        {showDescription && description && (
          <p className={cx(
            "text-gray-600 dark:text-gray-400",
            sizeClass.description
          )}>
            {description}
          </p>
        )}

        {/* Render categorized FAQs if multiple categories exist */}
        {hasCategories ? (
          <div className="space-y-6">
            {Object.entries(categories).map(([category, categoryItems]) => (
              <div key={category}>
                <h4 className={cx(
                  "font-medium text-gray-900 dark:text-white",
                  sizeClass.categoryTitle
                )}>
                  {category}
                </h4>
                <Accordion {...accordionProps}>
                  {categoryItems.map((item, index) => (
                    <AccordionItem
                      key={index}
                      value={`${category}-${index}`}
                      className={cx(
                        "overflow-hidden border-b border-gray-200 dark:border-gray-800 first:mt-0",
                        theme.variant === "minimal" && "border-0"
                      )}
                    >
                      <AccordionTrigger className={cx(
                        "group flex flex-1 items-center justify-between font-medium leading-none",
                        sizeClass.question
                      )}
                        onClick={() => {
                          // Track FAQ interactions as a standalone GA event (not a resource event)
                          // We keep payload lean: interaction type and question text, plus delta timing
                          sendEvent({
                            action: 'faq_interact',
                            engagement_time_msec: getDeltaMs(),
                            params: { interaction_type: 'faq_toggle', question: item.question }
                          })
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {item.icon && (
                            <span className="flex-shrink-0 text-gray-400 dark:text-gray-600">
                              {item.icon}
                            </span>
                          )}
                          <span className="text-gray-900 dark:text-white">
                            {item.question}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className={cx(
                          "text-gray-600 dark:text-gray-400",
                          sizeClass.answer
                        )}>
                          {item.answer}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        ) : (
          /* Render flat list of FAQs if only one category exists */
          <Accordion {...accordionProps}>
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className={cx(
                  "overflow-hidden border-b border-gray-200 dark:border-gray-800 first:mt-0",
                  theme.variant === "minimal" && "border-0"
                )}
              >
                <AccordionTrigger className={cx(
                  "group flex flex-1 items-center justify-between font-medium leading-none",
                  sizeClass.question
                )}
                  onClick={() => {
                    sendEvent({
                      action: 'faq_interact',
                      engagement_time_msec: getDeltaMs(),
                      params: { interaction_type: 'faq_toggle', question: item.question }
                    })
                  }}
                >
                  <div className="flex items-center gap-2">
                    {item.icon && (
                      <span className="flex-shrink-0 text-gray-400 dark:text-gray-600">
                        {item.icon}
                      </span>
                    )}
                    <span className="text-gray-900 dark:text-white">
                      {item.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={cx(
                    "text-gray-600 dark:text-gray-400",
                    sizeClass.answer
                  )}>
                    {item.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </>
  )
})

FAQ.displayName = "FAQ"